import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Form, FormInstance, Space, Spin } from 'antd';
import { ReactComponent as ErrorSignIcon } from 'assets/images/icons/errorSignIcon.svg';
import { ReactComponent as InfoMessageIcon } from 'assets/images/icons/infoMessageIcon.svg';
import { ReactComponent as RocketIcon } from 'assets/images/icons/rocketIcon.svg';
import { BillingForm } from 'components/AccountComponent/billing-form';
import { GrayCard, StyledCard } from 'components/Custom/card';
import { NumericInput } from 'components/Custom/inputs';
import {
  CardDescBoldSpan,
  CardDescSpan,
  GreenSpan,
  TableItemSpan,
} from 'components/Custom/spans';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import { UpgradeMessageSource } from 'store/api/enums';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { AddOnsType, Currency, SubscriptionPlan } from 'store/api/enums';
import { turnModalOff, turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { BillingCheckout } from 'store/billing/models/billingCheckout';
import { BillingFormRequest } from 'store/billing/models/billingFormRequest';
import Features from 'store/billing/models/features.json';
import { PlanPricingDetails } from 'store/billing/models/planPricingDetails';
import { SelectedPlan } from 'store/billing/types';
import { Country, CountryFlag } from 'store/common/models';
import {
  getCurrencySymbol,
  getPriceByCurrency,
} from 'utils/calcAmountCurrency';
import { useNumberFormat } from 'utils/numberFormat';
import { PlanViewer } from './components/PlanViewer';
interface CheckoutComponentProps {
  selectedPlan: SelectedPlan;
  backToChangePlan: () => void;
  continueToCheckout: (billing: BillingCheckout) => void;
  countries: Country[];
  countriesExternalData: CountryFlag[];
  changeCurrency: (newCurrency: Currency) => void;
  isLoading?: boolean;
  subscriptionPlanPricingDetails?: PlanPricingDetails;
  hideBillingDetails: boolean;
  billingDetails?: BillingFormRequest;
  saveBillingDetails: (billingDetails: BillingFormRequest) => void;
  countryISO: string;
  userVatNumber: string;
  setCountryISO: (countryISO: string) => void;
  setUserVatNumber: (userVatNumber: string) => void;
  billingForm: FormInstance<any>;
  vatPercentage: number;
  setVATPercentage: (vatPercentage: number) => void;
  isEurCountry: boolean;
}

export const CheckoutComponent: React.FC<CheckoutComponentProps> = ({
  selectedPlan,
  backToChangePlan,
  continueToCheckout,
  countries,
  countriesExternalData,
  changeCurrency,
  isLoading,
  subscriptionPlanPricingDetails,
  hideBillingDetails,
  billingDetails,
  saveBillingDetails,
  countryISO,
  userVatNumber,
  setCountryISO,
  setUserVatNumber,
  billingForm,
  vatPercentage,
  setVATPercentage,
  isEurCountry,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { formatNumber } = useNumberFormat();

  const handleInfoClick = () => {
    const modalData: ModalData = {
      title: '',
      type: WindowType.WARNING,
      component: (
        <UpgradeMessage
          source={UpgradeMessageSource.VAT_CHECKER_ACTIVATE}
          onClose={() => dispatch(turnModalOff())}
          isFromCheckout={true}
        />
      ),
      maskClosable: false,
    };
    dispatch(turnModalOn(modalData));
  };

  const [additionalEntities, setAdditionalEntities] = React.useState(0);
  const [featuresOfCurrentPlan, setFeaturesOfCurrentPlan] = React.useState<
    string[]
  >([]);
  const [vatNumberCheckerPerEntity, setVatNumberCheckerPerEntity] =
    React.useState(1);

  const [isAllowed, setIsAllowed] = React.useState(false);
  const [, setNumberOfFreeEntities] = React.useState(0);
  const [numberOfAdditionalEntities, setNumberOfAdditionalEntities] =
    React.useState(0);
  const [isCheckoutProcessing, setIsCheckoutProcessing] = React.useState(false);

  // Add validation error states
  const [additionalEntitiesError, setAdditionalEntitiesError] =
    React.useState(false);
  const [vatCheckerError, setVatCheckerError] = React.useState(false);

  const values = Form.useWatch([], billingForm);

  // Simple timer to prevent rapid clicking
  const handleCheckoutClick = () => {
    setIsCheckoutProcessing(true);
    let billingValues: BillingFormRequest = billingForm.getFieldsValue();
    saveBillingDetails({ ...billingValues, vatNumber: userVatNumber });
    const vatNumber = isEurCountry ? userVatNumber : '';
    const companyName =
      !isEurCountry && !!userVatNumber
        ? billingValues.companyName + `(TaxNo:${userVatNumber})`
        : billingValues.companyName;

    const { ...billingValuesWithoutPhone } =
      billingValues as BillingFormRequest & {
        phonePrefix?: string;
        phone?: string;
      };
    delete billingValuesWithoutPhone.phonePrefix;
    delete billingValuesWithoutPhone.phone;
    let billing: BillingCheckout = {
      ...billingValuesWithoutPhone,
      vatNumber: vatNumber,
      additionalEntities,
      vatnumberchecker: vatNumberCheckerPerEntity,
      companyName: companyName,
    } as BillingCheckout;

    continueToCheckout(billing);

    // Reset after 2 seconds to prevent rapid clicking
    setTimeout(() => {
      setIsCheckoutProcessing(false);
    }, 2000);
  };

  React.useEffect(() => {
    billingForm
      .validateFields({ validateOnly: true })
      .then(() => setIsAllowed(true))
      .catch(() => setIsAllowed(false));
  }, [billingForm, values]);

  React.useEffect(() => {
    if (billingDetails) {
      billingForm.setFieldsValue({
        addressLine1: billingDetails.addressLine1,
        addressLine2: billingDetails.addressLine2,
        town: billingDetails.town,
        country: billingDetails.country,
        zipCode: billingDetails.zipCode,
        //phonePrefix: billingDetails.phonePrefix,
        //phone: billingDetails.phone,
        email: billingDetails.email,
        currency: selectedPlan?.selectedCurrency,
        vatNumber: billingDetails.vatNumber,
        companyName: billingDetails.companyName,
      });
    }
  }, [billingDetails]);

  // Add validation error tracking for additional entities
  React.useEffect(() => {
    if (
      subscriptionPlanPricingDetails?.minimumAdditionalEntities !== undefined
    ) {
      setAdditionalEntitiesError(
        additionalEntities <
          subscriptionPlanPricingDetails.minimumAdditionalEntities
      );
    }
  }, [
    additionalEntities,
    subscriptionPlanPricingDetails?.minimumAdditionalEntities,
  ]);

  // Add validation error tracking for VAT checker
  React.useEffect(() => {
    if (
      subscriptionPlanPricingDetails?.minimumVatCheckerEntities !== undefined
    ) {
      setVatCheckerError(
        vatNumberCheckerPerEntity <
          subscriptionPlanPricingDetails.minimumVatCheckerEntities
      );
    }
  }, [
    vatNumberCheckerPerEntity,
    subscriptionPlanPricingDetails?.minimumVatCheckerEntities,
  ]);

  React.useEffect(() => {
    if (subscriptionPlanPricingDetails) {
      setFeaturesOfCurrentPlan(
        getFeaturesOfCurrentPlan(subscriptionPlanPricingDetails?.name + ' Plan')
      );
      setAdditionalEntities(
        subscriptionPlanPricingDetails?.minimumAdditionalEntities
      );
      setVatNumberCheckerPerEntity(
        subscriptionPlanPricingDetails?.minimumVatCheckerEntities
      );

      // Initialize error states
      setAdditionalEntitiesError(false);
      setVatCheckerError(false);

      const connectedOrganisationsFeature = Features.find(
        (fet) => fet.feature?.toLowerCase() === 'free entities included'
      );

      const numberOfFreeEntities =
        connectedOrganisationsFeature?.[
          subscriptionPlanPricingDetails?.name.toLowerCase()
        ];
      setNumberOfFreeEntities(numberOfFreeEntities);

      /* if (hideBillingDetails) {
        const vatPercentage =
          (subscriptionPlanPricingDetails?.pricing.vat /
            subscriptionPlanPricingDetails?.pricing?.subtotal) *
          100;
        setVATPercentage(vatPercentage);
      } */
      getAdditionalEntities(subscriptionPlanPricingDetails?.name.toLowerCase());
    }
  }, [subscriptionPlanPricingDetails]);

  const getAdditionalEntities = (planType) => {
    switch (planType) {
      case 'small':
        setNumberOfAdditionalEntities(3);
        break;
      case 'medium':
        setNumberOfAdditionalEntities(8);
        break;
      case 'large':
        setNumberOfAdditionalEntities(15);
        break;
    }
  };

  const getFeaturesOfCurrentPlan = (currentPlan: string) => {
    const plan = currentPlan.split(' ')[0]?.toLowerCase();

    const integrationsIncludedIndex = Features.findIndex(
      (fet) => fet.feature?.toLowerCase() === 'integrations included'
    );
    const lastIntegrationIndex = Features.findIndex(
      (item, index) =>
        index > integrationsIncludedIndex && Object.keys(item).length == 0
    );
    const integrationFeatures = Features.slice(
      integrationsIncludedIndex + 1,
      lastIntegrationIndex
    );
    const thirdParties = integrationFeatures
      .filter((feature) => feature[plan])
      .map((feature) => feature.feature);

    const planFeatures = Features.map((feature, index) => {
      if (
        Object.keys(feature).length > 0 &&
        !feature.isCategoryFeature &&
        feature[plan] !== 'n/a' &&
        feature[plan] !== false &&
        !feature.feature?.includes('coming soon') &&
        !feature.feature?.toLowerCase().startsWith('additional') &&
        (index < integrationsIncludedIndex || index > lastIntegrationIndex)
      ) {
        //if (feature.feature?.toLowerCase() == 'free entities included') {
        if (typeof feature[plan] === 'boolean') {
          return feature.feature;
        }
        if (feature[plan] == 1) {
          return `${feature[plan]} ${feature.feature
            ?.replace('Entities', 'Entity')
            .replace('(up to)', '')}`;
        } else {
          return feature.feature?.replace('(up to)', `up to ${feature[plan]}`);
        }
      }
    });
    planFeatures.push(`Integrations Included: ${thirdParties.join(', ')}`);
    return planFeatures.filter((item) => item !== undefined);
  };

  const calcAddOns = (addOnsType: AddOnsType): number => {
    const currPlan = subscriptionPlanPricingDetails?.name;
    if (currPlan) {
      const vatNumberCheckerAddOnPrice: any = getPriceByCurrency(
        selectedPlan?.selectedCurrency,
        29,
        currPlan?.toLowerCase(),
        false
      );
      const additionalEntitiesAddOnPrice: any = getPriceByCurrency(
        selectedPlan?.selectedCurrency,
        2,
        currPlan?.toLowerCase(),
        false
      );

      if (
        addOnsType == AddOnsType.VAT_NUMBER_CHECKER &&
        vatNumberCheckerAddOnPrice != 'n/a'
      ) {
        return vatNumberCheckerPerEntity * Number(vatNumberCheckerAddOnPrice);
      } else if (
        addOnsType == AddOnsType.ADDITIONAL_ENTITIES &&
        additionalEntitiesAddOnPrice != 'n/a'
      ) {
        return additionalEntities * Number(additionalEntitiesAddOnPrice);
      } else {
        return 0;
      }
    }
    return 0;
  };

  const calcVATAmount = (): number => {
    const subTotalAmount = calcSubTotal();
    return subTotalAmount * (vatPercentage / 100);
  };

  const calcSubTotal = (): number => {
    const currPlan = subscriptionPlanPricingDetails?.name;
    const vatNumAddOn = calcAddOns(AddOnsType.VAT_NUMBER_CHECKER);
    const addEntitiesAddOn = calcAddOns(AddOnsType.ADDITIONAL_ENTITIES);
    if (currPlan) {
      const planPrice = getPriceByCurrency(
        selectedPlan?.selectedCurrency,
        27,
        currPlan?.toLowerCase(),
        false
      );
      return vatNumAddOn + addEntitiesAddOn + Number(planPrice);
    }
    return 0;
  };

  const calcTotal = (): number => {
    return calcVATAmount() + calcSubTotal();
  };

  const renderPlanFeaturesCard = () => {
    const currPlan = subscriptionPlanPricingDetails?.name;
    return (
      <StyledCard
        width="320px"
        className="checkout-selected-plan-panel"
        style={{ marginTop: '30px' }}
      >
        <CardDescBoldSpan
          fontSize="18px"
          fontWeight="700"
          $lineHeight="24px"
          style={{ display: 'block', margin: '20px 0 24px' }}
        >
          {t('billing.your-selected-plan')}
        </CardDescBoldSpan>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingBottom: '30px',
            borderBottom: '1px solid var(--color-border, #dbeae3)',
            marginBottom: '25px',
          }}
        >
          <TableItemSpan fontSize="16px" fontWeight="600" $lineHeight="22px">
            {currPlan}
          </TableItemSpan>
          <GreenSpan fontSize="14px" fontWeight="600" $lineHeight="22px">
            {currPlan
              ? getPriceByCurrency(
                  selectedPlan?.selectedCurrency,
                  27,
                  currPlan?.toLowerCase()
                )
              : ''}
          </GreenSpan>
        </div>
        <Space
          direction="vertical"
          style={{
            width: '100%',
            paddingBottom: '25px',
            borderBottom: '1px solid var(--color-border, #dbeae3)',
            marginBottom: '25px',
          }}
        >
          <TableItemSpan fontSize="16px" fontWeight="600" $lineHeight="22px">
            {t('billing.add-ons')}
          </TableItemSpan>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <TableItemSpan
              fontSize="12px"
              fontWeight="400"
              $lineHeight="22px"
              fontColor='var(--color-text-muted, #5a6a6a)'
            >
              {t('navigation.vat-number-checker')}{' '}
              <TableItemSpan fontSize="12px" fontWeight="600" $lineHeight="22px">
                x{vatNumberCheckerPerEntity}
              </TableItemSpan>
            </TableItemSpan>
            <GreenSpan fontSize="14px" fontWeight="600" $lineHeight="22px">
              {`${getCurrencySymbol(
                selectedPlan?.selectedCurrency
              )} ${formatNumber(calcAddOns(AddOnsType.VAT_NUMBER_CHECKER))}`}
            </GreenSpan>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <TableItemSpan
              fontSize="12px"
              fontWeight="400"
              $lineHeight="22px"
              fontColor='var(--color-text-muted, #5a6a6a)'
            >
              {t('billing.additional-entities')}{' '}
              <TableItemSpan fontSize="12px" fontWeight="600" $lineHeight="22px">
                x{additionalEntities}
              </TableItemSpan>
            </TableItemSpan>
            <GreenSpan fontSize="14px" fontWeight="600" $lineHeight="22px">
              {`${getCurrencySymbol(
                selectedPlan?.selectedCurrency
              )} ${formatNumber(calcAddOns(AddOnsType.ADDITIONAL_ENTITIES))}`}
            </GreenSpan>
          </div>
        </Space>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <TableItemSpan fontSize="12px" fontWeight="400" $lineHeight="30px">
            {t('billing.subtotal')}
          </TableItemSpan>
          <TableItemSpan fontSize="12px" fontWeight="400" $lineHeight="30px">
            {`${getCurrencySymbol(
              selectedPlan?.selectedCurrency
            )} ${formatNumber(calcSubTotal())}`}
          </TableItemSpan>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <TableItemSpan fontSize="12px" fontWeight="400" $lineHeight="30px">
            {t('main.VAT')}
          </TableItemSpan>
          <TableItemSpan fontSize="12px" fontWeight="400" $lineHeight="30px">
            {`${getCurrencySymbol(
              selectedPlan?.selectedCurrency
            )} ${formatNumber(calcVATAmount())}`}
          </TableItemSpan>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <TableItemSpan fontSize="14px" fontWeight="700" $lineHeight="30px">
            {t('billing.total')}
          </TableItemSpan>
          <TableItemSpan fontSize="14px" fontWeight="700" $lineHeight="30px">
            {`${getCurrencySymbol(
              selectedPlan?.selectedCurrency
            )} ${formatNumber(calcTotal())}`}
          </TableItemSpan>
        </div>
        <ScopeButton
          type="primary"
          width="246px"
          height="32px"
          fontSize="13px"
          lineheight="20px"
          style={{ margin: '0 12px', marginTop: '18px' }}
          onClick={handleCheckoutClick}
          disabled={
            isCheckoutProcessing ||
            isLoading ||
            /* (!userVatNumber && !isLoading && isEurCountry) || */
            (!isAllowed && !hideBillingDetails) ||
            additionalEntitiesError ||
            vatCheckerError
          }
        >
          {isCheckoutProcessing
            ? t('billing.processing')
            : t('billing.continue-to-checkout')}
        </ScopeButton>
      </StyledCard>
    );
  };

  const getNextPlanLabel = (currentPlan: SubscriptionPlan) => {
    switch (currentPlan) {
      case SubscriptionPlan.SMALL_PLAN:
        return `${t('billing.plan-medium-label')} ${t('billing.plan-suffix')}`;
      case SubscriptionPlan.MEDIUM_PLAN:
        return `${t('billing.plan-large-label')} ${t('billing.plan-suffix')}`;
      case SubscriptionPlan.LARGE_PLAN:
        return t('billing.plan-enterprise-label');
      default:
        return '';
    }
  };

  const renderCheckoutForm = () => {
    return (
      <div
        style={{
          padding: '0 40px',
          width: '100%',
          margin: 'auto',
          maxWidth: 1100,
          marginTop: '30px',
        }}
      >
        <Space
          direction="vertical"
          style={{ width: '100%', rowGap: 25, marginBottom: 30 }}
        >
          <StyledCard
            width="100%"
            style={{
              overflow: 'auto',
              padding: '10px 20px',
            }}
          >
            <PlanViewer
              subscriptionPlanPricingDetails={subscriptionPlanPricingDetails}
              isLoading={isLoading}
              backToChangePlan={backToChangePlan}
            />
            <div className="divider-20" />
            <div className="divider-20" />
            <div
              style={{
                marginBottom: '30px',
                borderBottom: '1px solid var(--color-border, #dbeae3)',
              }}
            >
              <TableItemSpan
                fontSize="18px"
                $lineHeight="24px"
                fontWeight="600"
              >
                {t('organisation.entities')}
              </TableItemSpan>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexFlow: 'row',
                  alignContent: 'normal',
                  paddingBottom: '15px',
                  paddingTop: '15px',
                }}
              >
                <TableItemSpan
                  fontSize="14px"
                  $lineHeight="42px"
                  fontWeight="400"
                >
                  {t('billing.additional-entities')}{' '}
                  <CardDescSpan>
                    {t('billing.additional-entities-checkout-hint')}
                  </CardDescSpan>
                </TableItemSpan>
                <NumericInput
                  min={
                    subscriptionPlanPricingDetails?.minimumAdditionalEntities
                  }
                  max={500}
                  addonAfter={
                    <PlusOutlined
                      disabled={additionalEntities >= 500}
                      style={{ color: 'var(--color-primary, #41aaaf)' }}
                      onClick={() =>
                        setAdditionalEntities(additionalEntities + 1)
                      }
                    />
                  }
                  addonBefore={
                    <MinusOutlined
                      disabled={additionalEntities <= 0}
                      style={{ color: 'var(--color-primary, #41aaaf)' }}
                      onClick={() =>
                        setAdditionalEntities(additionalEntities - 1)
                      }
                    />
                  }
                  controls={false}
                  value={additionalEntities}
                  onChange={(value: number) =>
                    setAdditionalEntities(
                      value === null || value === undefined ? 0 : value
                    )
                  }
                />
              </div>
              {additionalEntities <
                (subscriptionPlanPricingDetails?.minimumAdditionalEntities ??
                  0) && (
                <StyledCard
                  $stopshadowbox
                  $padding="15px 24px"
                  style={{
                    margin: '0px 0px 30px',
                    width: '100%',
                    backgroundColor: 'var(--color-layout-bg, #E9F6F7)',
                    border: '1px solid var(--color-border, #dbeae3)',
                  }}
                >
                  <Space style={{ columnGap: 16 }}>
                    <ErrorSignIcon />
                    <TableItemSpan
                      fontSize="12px"
                      $lineHeight="16px"
                      fontWeight="400"
                    >
                      <Trans
                        i18nKey="billing.downgrade-delete-entities"
                        components={{
                          1: (
                            <Link
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                textDecoration: 'underline',
                                color: 'var(--color-text-dark, #042a2a)',
                              }}
                              to="/entities"
                            />
                          ),
                        }}
                      />
                    </TableItemSpan>
                  </Space>
                </StyledCard>
              )}
              {subscriptionPlanPricingDetails?.name !=
              SubscriptionPlan.ENTERPRISE_PLAN ? (
                <GrayCard style={{ margin: '0px 0px 30px' }}>
                  <Space style={{ columnGap: 16 }}>
                    <RocketIcon width={40} height={40} />
                    <TableItemSpan
                      fontSize="12px"
                      $lineHeight="24px"
                      fontWeight="400"
                    >
                      <Trans
                        i18nKey="billing.upgrade-additional-entities-recommendation"
                        values={{
                          count: numberOfAdditionalEntities,
                          nextPlan: getNextPlanLabel(
                            subscriptionPlanPricingDetails?.name as SubscriptionPlan
                          ),
                        }}
                        components={{
                          1: (
                            <Link
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                color: 'var(--color-primary, #41aaaf)',
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '24px',
                                textDecoration: 'underline',
                                textUnderlineOffset: '3px',
                              }}
                              to="/pricing"
                              onClick={backToChangePlan}
                            />
                          ),
                          2: (
                            <TableItemSpan
                              fontSize="12px"
                              $lineHeight="24px"
                              fontWeight="600"
                            />
                          ),
                        }}
                      />
                    </TableItemSpan>
                  </Space>
                </GrayCard>
              ) : (
                <GrayCard style={{ margin: '0px 0px 30px' }}>
                  <Space style={{ columnGap: 16 }}>
                    <RocketIcon width={40} height={40} />
                    <TableItemSpan
                      fontSize="12px"
                      $lineHeight="24px"
                      fontWeight="400"
                    >
                      If you would like to add more entities to your existing
                      plan, we recommend you reach out to to your account
                      manager to review your package.
                    </TableItemSpan>
                  </Space>
                </GrayCard>
              )}
            </div>
            <div
              style={{
                marginBottom: '30px',
                borderBottom: '1px solid var(--color-border, #dbeae3)',
              }}
            >
              <TableItemSpan
                fontSize="18px"
                $lineHeight="24px"
                fontWeight="400"
              >
                {t('billing.add-ons')}
              </TableItemSpan>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexFlow: 'row',
                  alignContent: 'normal',
                  paddingBottom: '15px',
                  paddingTop: '15px',
                }}
              >
                <TableItemSpan
                  fontSize="14px"
                  $lineHeight="42px"
                  fontWeight="400"
                >
                  {t('navigation.vat-number-checker')}{' '}
                  <CardDescSpan>{`(${t('billing.per-entity')})`}</CardDescSpan>
                  <span
                    style={{
                      display: 'inline-flex',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s ease',
                      position: 'relative',
                      top: '4px',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.opacity = '0.7')
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    onClick={handleInfoClick}
                  >
                    <InfoMessageIcon
                      style={{
                        width: '20px',
                        height: '20px',
                        marginLeft: '4px',
                      }}
                    />
                  </span>
                </TableItemSpan>
                <NumericInput
                  min={
                    subscriptionPlanPricingDetails?.minimumVatCheckerEntities
                  }
                  max={500}
                  addonAfter={
                    <PlusOutlined
                      disabled={vatNumberCheckerPerEntity >= 500}
                      style={{ color: 'var(--color-primary, #41aaaf)' }}
                      onClick={() =>
                        setVatNumberCheckerPerEntity(
                          vatNumberCheckerPerEntity + 1
                        )
                      }
                    />
                  }
                  addonBefore={
                    <MinusOutlined
                      disabled={vatNumberCheckerPerEntity <= 0}
                      style={{ color: 'var(--color-primary, #41aaaf)' }}
                      onClick={() =>
                        setVatNumberCheckerPerEntity(
                          vatNumberCheckerPerEntity - 1
                        )
                      }
                    />
                  }
                  controls={false}
                  value={vatNumberCheckerPerEntity}
                  onChange={(value: number) =>
                    setVatNumberCheckerPerEntity(
                      value === null || value === undefined ? 0 : value
                    )
                  }
                />
              </div>
              {vatNumberCheckerPerEntity <
                (subscriptionPlanPricingDetails?.minimumVatCheckerEntities ??
                  0) && (
                <StyledCard
                  $stopshadowbox
                  $padding="15px 24px"
                  style={{
                    margin: '0px 0px 30px',
                    width: '100%',
                    backgroundColor: 'var(--color-layout-bg, #E9F6F7)',
                    border: '1px solid var(--color-border, #dbeae3)',
                  }}
                >
                  <Space style={{ columnGap: 16 }}>
                    <ErrorSignIcon />
                    <TableItemSpan
                      fontSize="12px"
                      $lineHeight="16px"
                      fontWeight="400"
                    >
                      <Trans
                        i18nKey="billing.downgrade-deactivate-vat-checker"
                        components={{
                          1: (
                            <Link
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                textDecoration: 'underline',
                                color: 'var(--color-text-dark, #042a2a)',
                              }}
                              to="/vies"
                            />
                          ),
                        }}
                      />
                    </TableItemSpan>
                  </Space>
                </StyledCard>
              )}
            </div>
          </StyledCard>
          {!hideBillingDetails && (
            <StyledCard
              width="100%"
              style={{
                overflow: 'auto',
              }}
            >
              <BillingForm
                billingForm={billingForm}
                countries={countries}
                countriesExternalData={countriesExternalData}
                isCheckoutForm={true}
                changeCurrency={changeCurrency}
                isLoading={isLoading}
                isAllowed={isAllowed}
                userVatNumber={userVatNumber}
                setCountryISO={setCountryISO}
                setUserVatNumber={setUserVatNumber}
                isEurCountry={isEurCountry}
              />
            </StyledCard>
          )}
        </Space>
      </div>
    );
  };

  return (
    <Spin spinning={isLoading || !subscriptionPlanPricingDetails} size="large">
      <div className="billing-page">
        <div
          style={{
            width: 'calc(100vw - 575px)',
          }}
        >
          {renderCheckoutForm()}
        </div>

        <div
          style={{
            height: '100vh',
            position: 'fixed',
            float: 'right',
            right: 40,
          }}
        >
          {renderPlanFeaturesCard()}
        </div>
      </div>
    </Spin>
  );
};
